import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fade, Typography } from '@mui/material';
import { ARCADE_THEME } from './arcadeTheme';

export const ARCADE_TOAST_DURATION_MS = 2500;
export const ARCADE_TOAST_FADE_MS = 300;

/**
 * Transient arcade toast. Timers are only cleared on unmount or when a new
 * toast replaces the current one — never from unrelated effect cleanups.
 */
export function useArcadeToast({ durationMs = ARCADE_TOAST_DURATION_MS } = {}) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('info');
    const hideTimerRef = useRef(null);
    const fadeTimerRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        if (fadeTimerRef.current) {
            clearTimeout(fadeTimerRef.current);
            fadeTimerRef.current = null;
        }
    }, []);

    const dismiss = useCallback(() => {
        clearTimers();
        setOpen(false);
        setMessage('');
    }, [clearTimers]);

    const showToast = useCallback((msg, sev = 'info') => {
        clearTimers();
        setMessage(msg);
        setSeverity(sev);
        setOpen(true);
        hideTimerRef.current = setTimeout(() => {
            setOpen(false);
            fadeTimerRef.current = setTimeout(() => {
                setMessage('');
                fadeTimerRef.current = null;
            }, ARCADE_TOAST_FADE_MS);
            hideTimerRef.current = null;
        }, durationMs);
    }, [clearTimers, durationMs]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    return { open, message, severity, showToast, dismiss };
}

export function ArcadeToast({ open, message, severity = 'info' }) {
    const border = severity === 'success' ? ARCADE_THEME.primary : ARCADE_THEME.border;
    const dot = severity === 'success'
        ? ARCADE_THEME.primary
        : severity === 'error'
            ? ARCADE_THEME.accent
            : '#3b82f6';

    return (
        <Fade in={Boolean(open)}>
            <Box
                data-testid="arcade-toast"
                sx={{
                    position: 'fixed',
                    top: 90,
                    right: 24,
                    zIndex: 2000,
                    bgcolor: 'rgba(24, 24, 27, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${border}`,
                    borderRadius: 2,
                    p: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minWidth: 300,
                }}
            >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />
                <Typography sx={{ color: 'white', fontWeight: 500 }}>{message}</Typography>
            </Box>
        </Fade>
    );
}
